import React, { useState, useEffect, useRef } from 'react';
import {
  VStack,
  HStack,
  Avatar,
  Box,
  Text,
  Textarea,
  Badge,
  Checkbox,
  Button,
  useToast,
  Flex,
  Input,
  IconButton,
  Link,
  Spinner,
} from '@chakra-ui/react';
import { AttachmentIcon, CloseIcon } from '@chakra-ui/icons';
import {
  fetchCardComments,
  addCardComment,
  uploadCommentAttachment,
} from '../../../../ProjectBoardServices/BoardModalAPI/LeftModalAPI/CommentsApi';
import { fetchAdmins } from '../../../../ProjectBoardServices/BoardModalAPI/RightModalAPI/InfoTabAPI/AssigneeAPI';

const CommentsTab = ({ task }) => {
  const cardId = task?.id;
  const toast = useToast();
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const mentionRef = useRef(null);

  const [comments, setComments] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [manualSubmit, setManualSubmit] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Mention states
  const [admins, setAdmins] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState(null);

  // Fetch admins once
  useEffect(() => {
    const loadAdmins = async () => {
      try {
        const res = await fetchAdmins(0);
        if (res?.data?.admins) {
          setAdmins(res.data.admins);
        }
      } catch (err) {
        console.error("Failed to load admins for mentions", err);
      }
    };
    loadAdmins();
  }, []);

  // Fetch comments
  useEffect(() => {
    getComments();
  }, []);

  const getComments = async () => {
    setIsLoading(true);
    try {
      const response = await fetchCardComments(cardId, 1);
      const data = response.data;

      if (data && Array.isArray(data)) setComments(data);
      else if (data?.comments && Array.isArray(data.comments)) setComments(data.comments);
      else if (data?.data && Array.isArray(data.data)) setComments(data.data);
      else setComments([]);
    } catch (error) {
      console.log('Error fetching comments:', error);
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCommentTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
      return `${Math.floor(diffInMinutes / 1440)} days ago`;
    } catch {
      return 'Just now';
    }
  };

  const uploadAttachment = async (file, notes = '') => {
    setIsUploading(true);
    try {
      if (!file || !(file instanceof File)) throw new Error('Invalid file');

      const formData = new FormData();
      formData.append('media', file);
      formData.append('notes', notes || `Attachment: ${file.name}`);

      const response = await uploadCommentAttachment(cardId, formData);
      const result = response.data;

      toast({ title: 'Attachment uploaded', status: 'success', duration: 2000 });

      return {
        attachmentId: result.data?.attachmentId || result.attachmentId,
        url: result.data?.key
          ? `https://s3.ap-south-1.amazonaws.com/com.makemyhouse.storage/${result.data.key}`
          : result.url || '',
        filename: file.name,
      };
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Upload failed', status: 'error', duration: 3000 });
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const addComment = async (commentText, attachmentData = null) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      let finalComment = commentText;

      if (attachmentData?.attachmentId) {
        finalComment += `![${attachmentData.filename}]({${attachmentData.attachmentId}}[)*\\n](${attachmentData.url || ''})*[${attachmentData.filename}](${attachmentData.url || ''})*\\n)`;
      }

      formData.append('comment', finalComment);
      if (manualSubmit) formData.append('sendToClient', 'true');

      const response = await addCardComment(cardId, formData);

      toast({ title: 'Comment added', status: 'success', duration: 2000 });
      await getComments();
      return response.data;
    } catch (error) {
      console.error('Add comment error:', error);
      toast({ title: 'Failed to add comment', status: 'error', duration: 3000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);

    const notes = inputValue || `Attachment: ${file.name}`;
    const attachmentResult = await uploadAttachment(file, notes);

    if (attachmentResult?.attachmentId) {
      await addComment(inputValue.trim() || 'File uploaded', attachmentResult);
      setInputValue('');
      setSelectedFile(null);
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!inputValue.trim() && !selectedFile) return;

    let attachmentResult = null;

    if (selectedFile) {
      const notes = inputValue || `Attachment: ${selectedFile.name}`;
      attachmentResult = await uploadAttachment(selectedFile, notes);
      if (!attachmentResult?.attachmentId) return;
    }

    await addComment(inputValue.trim() || 'Comment added', attachmentResult);

    setInputValue('');
    setSelectedFile(null);
    fileInputRef.current.value = '';
  };

  const handleKeyDown = (e) => {
    if (e.ctrlKey && e.key === 'Enter') {
      handleSubmit();
    }
    if (e.key === 'Escape') {
      setShowMentionDropdown(false);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    fileInputRef.current.value = '';
  };

  const handleTextareaChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;

    setInputValue(value);

    const textBeforeCursor = value.slice(0, cursorPos);
    const lastAt = textBeforeCursor.lastIndexOf('@');

    if (lastAt === -1) {
      setShowMentionDropdown(false);
      return;
    }

    const searchText = textBeforeCursor.slice(lastAt + 1);

    if (searchText.includes(' ') || searchText.includes('\n')) {
      setShowMentionDropdown(false);
      return;
    }

    setMentionSearch(searchText.trim().toLowerCase());
    setMentionPosition(lastAt);
    setShowMentionDropdown(true);

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(cursorPos, cursorPos);
      }
    });
  };

  const insertMention = (admin) => {
    if (!textareaRef.current || mentionPosition === null) return;

    const name = `${admin.admin_firstname || ''} ${admin.admin_lastname || ''}`.trim();
    const mentionText = `@${name} `;

    const before = inputValue.slice(0, mentionPosition);
    const after = inputValue.slice(mentionPosition + mentionSearch.length + 1);

    const newValue = before + mentionText + after;
    setInputValue(newValue);

    const newCursorPos = mentionPosition + mentionText.length;

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    });

    setShowMentionDropdown(false);
    setMentionSearch('');
    setMentionPosition(null);
  };

  const filteredAdmins = admins.filter((admin) => {
    const fullName = `${admin.admin_firstname || ''} ${admin.admin_lastname || ''}`.toLowerCase().trim();
    const email = admin.admin_email?.toLowerCase() || '';
    return fullName.includes(mentionSearch) || email.includes(mentionSearch);
  });

  return (
    <VStack align="stretch" spacing={4}>
      <HStack align="flex-start" spacing={3}>
        <Avatar size="sm" src="https://ui-avatars.com/api/?name=You" w="32px" h="32px" />

        <Box flex="1" position="relative">
          {selectedFile && (
            <HStack p={2} bg="blue.50" borderRadius="md" mb={2} justify="space-between">
              <HStack>
                <AttachmentIcon color="blue.500" />
                <Text fontSize="sm" color="blue.700">{selectedFile.name}</Text>
              </HStack>
              <IconButton
                size="xs"
                icon={<CloseIcon />}
                onClick={removeSelectedFile}
                variant="ghost"
                colorScheme="red"
              />
            </HStack>
          )}

          <Textarea
            ref={textareaRef}
            value={inputValue}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder="Add a comment... Use @ to mention someone"
            resize="vertical"
            minH="80px"
            fontSize="14px"
            border="1px solid #DFE1E6"
            _focus={{ borderColor: '#0065FF', boxShadow: '0 0 0 1px #0065FF' }}
          />

          {/* Mention Dropdown - Without Portal & Without Blur/Backdrop */}
          {showMentionDropdown && (
            <Box
              position="absolute"
              top="100%"
              left={0}
              width="100%"
              maxWidth="320px"
              mt={1}
              zIndex={1500}
              boxShadow="lg"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
              bg="white"
              maxH="260px"
              overflowY="auto"
            >
              {filteredAdmins.length > 0 ? (
                filteredAdmins.map((admin) => (
                  <HStack
                    key={admin.admin_id}
                    p={2}
                    cursor="pointer"
                    _hover={{ bg: 'blue.50' }}
                    onClick={() => insertMention(admin)}
                  >
                    <Avatar
                      size="xs"
                      name={`${admin.admin_firstname || ''} ${admin.admin_lastname || ''}`}
                    />
                    <Box>
                      <Text fontSize="sm" fontWeight="medium">
                        {admin.admin_firstname} {admin.admin_lastname}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {admin.admin_email}
                      </Text>
                    </Box>
                  </HStack>
                ))
              ) : (
                <Text fontSize="sm" color="gray.500" p={2}>
                  No matching users found
                </Text>
              )}
            </Box>
          )}

          <Input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            display="none"
            accept="*/*"
          />

          <Flex mt={2} mb={1} justify="space-between" align="center">
            <HStack spacing={4}>
              <Text fontSize="12px" color="#6B778C">
                <Text as="span" fontWeight="600">Pro tip:</Text> press{' '}
                <Badge px={1} py={0} fontSize="10px" bg="#F4F5F7">Ctrl + ⏎</Badge> to submit • Use @ to mention
              </Text>

              <Button
                size="xs"
                variant="ghost"
                leftIcon={<AttachmentIcon />}
                onClick={() => fileInputRef.current?.click()}
                isLoading={isUploading}
              >
                Attach
              </Button>
            </HStack>

            <Checkbox size="sm" isChecked={manualSubmit} onChange={(e) => setManualSubmit(e.target.checked)}>
              Send to Client
            </Checkbox>
          </Flex>

          {manualSubmit && (
            <HStack justify="flex-end" mt={1}>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={isSubmitting || isUploading}
                loadingText={isUploading ? 'Uploading...' : 'Submitting...'}
              >
                Submit
              </Button>
            </HStack>
          )}
        </Box>
      </HStack>

      <VStack spacing={4} align="stretch" maxH="400px" overflowY="auto" pr={2}>
        {isLoading && (
          <HStack justify="center" p={4}>
            <Spinner size="sm" />
            <Text fontSize="sm" color="gray.500">Loading comments...</Text>
          </HStack>
        )}

        {!isLoading && comments.length > 0 &&
          comments.map((comment, index) => (
            <HStack key={comment.id || index} align="flex-start" spacing={3}>
              <Avatar
                size="sm"
                src={comment.user?.avatar || comment.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.user?.name || comment.author || 'User')}`}
                w="32px"
                h="32px"
              />
              <VStack align="flex-start" spacing={1} flex="1">
                <HStack spacing={2}>
                  <Text fontSize="14px" fontWeight="600" color="#172B4D">
                    {comment.user?.name || comment.author || 'User'}
                  </Text>
                  <Text fontSize="12px" color="#6B778C">
                    {formatCommentTime(comment.createdAt || comment.time || comment.timestamp)}
                  </Text>
                </HStack>
                <Text fontSize="14px" color="#172B4D" lineHeight="1.4">
                  {comment.content || comment.text || comment.comment || comment.message}
                </Text>
                {comment.attachments?.map((att, idx) => (
                  <Link key={idx} href={att.url || att.link} color="blue.500" fontSize="12px" isExternal>
                    📎 {att.name || att.filename || 'Attachment'}
                  </Link>
                ))}
              </VStack>
            </HStack>
          ))}

        {!isLoading && comments.length === 0 && (
          <Box textAlign="center" py={8}>
            <Text fontSize="16px" color="#6B778C" fontWeight="500" mb={2}>No comments yet</Text>
            <Text fontSize="14px" color="#97A0AF">Be the first to add a comment</Text>
          </Box>
        )}
      </VStack>
    </VStack>
  );
};

export default CommentsTab;